import { FC, useEffect, useLayoutEffect, useReducer, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { twMerge } from 'tailwind-merge';

import { CrossIcon } from '../../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../../components/app-formatted-message/index';
import Button from '../../../../../components/button/index';
import Loader from '../../../../../components/loader/index';
import { ModalWrapper } from '../../../../../components/modal-wrapper/index';
import { SignaturePopupType } from '../../enums/signature-popup-type.const';
import { useReviewSignatureDocument } from '../../hooks/use-review-signature-document.hook';
import { SignaturePopupStore } from '../../store/signature-popup.store';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export const SignaturePreviewModal: FC = () => {
  const isOpen = SignaturePopupStore(({ open }) => open);
  const type = SignaturePopupStore(({ type }) => type);
  const metadata = SignaturePopupStore(({ metadata }) => metadata);
  const handleDismiss = SignaturePopupStore(({ handleDismiss }) => handleDismiss);

  const [numPages, setNumPages] = useState<null | number>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const pageWidthRef = useRef(0);
  const [, rerenderAfterMeasure] = useReducer((count: number) => count + 1, 0);

  const isActive = isOpen && type === SignaturePopupType.VIEW_DOCUMENT;
  const signatureId = metadata && 'signatureId' in metadata ? metadata.signatureId : null;

  const { previewUrl } = useReviewSignatureDocument(signatureId ?? '', isActive);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  useEffect(() => {
    setNumPages(null);
  }, [previewUrl]);

  useLayoutEffect(() => {
    const el = scrollAreaRef.current;
    if (!el) {
      return;
    }

    const measure = (): void => {
      const styles = window.getComputedStyle(el);
      const paddingX = parseFloat(styles.paddingLeft) + parseFloat(styles.paddingRight);
      const next = Math.max(0, Math.floor(el.clientWidth - paddingX));
      if (pageWidthRef.current !== next) {
        pageWidthRef.current = next;
        rerenderAfterMeasure();
      }
    };

    measure();

    const observer = new ResizeObserver(() => {
      measure();
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
    };
  }, [isOpen, previewUrl]);

  if (type !== SignaturePopupType.VIEW_DOCUMENT || !metadata || !('signatureId' in metadata)) {
    return null;
  }

  const { documentName } = metadata;
  const pageCount = numPages ?? 0;
  const pageWidth = pageWidthRef.current;

  return (
    <ModalWrapper
      className={twMerge(
        '!bottom-[25px] !left-[20px] !right-[20px] !top-[25px] !z-[61] !mx-auto flex !h-auto !w-auto !max-w-[990px] !translate-x-0 !translate-y-0 flex-col overflow-hidden rounded-xl bg-white shadow-lg',
      )}
      control={{
        open: isOpen,
        onOpenChange: (openState) => {
          if (!openState) {
            handleDismiss();
          }
        },
      }}
      onBgClick={handleDismiss}
      overlayClassName="z-[60]"
      side="CENTER"
    >
      <div className={twMerge('flex min-h-0 flex-1 flex-col', 'overflow-hidden')}>
        <div className="flex shrink-0 items-center justify-between px-6 py-4">
          <h2 className="min-w-0 truncate pr-2 text-[20px] font-medium text-gray-700">
            {documentName}
          </h2>
          <Button
            className="h-8 w-8 shrink-0 rounded-full border border-[#F2F2F2] p-0 shadow-[0_0_18.667px_0_rgba(0,0,0,0.08)]"
            onClick={handleDismiss}
            styleType="NONE"
            type="button"
          >
            <CrossIcon className="h-4 w-4" iconColor="#344054" />
          </Button>
        </div>

        <div
          className="custom-scrollbar relative flex min-h-0 min-w-0 flex-1 justify-center overflow-y-auto bg-white px-8 pt-2"
          ref={scrollAreaRef}
        >
          {previewUrl && pageWidth > 0 ? (
            <Document
              file={previewUrl}
              loading={null}
              onLoadSuccess={({ numPages: nextNumPages }) => {
                setNumPages(nextNumPages);
              }}
            >
              {Array.from({ length: pageCount }, (_, index) => (
                <div
                  className="mb-8 overflow-hidden rounded-2xl bg-white shadow-[0px_1px_2px_0px_rgba(16,24,40,0.06),0px_2px_3px_0px_rgba(16,24,40,0.10),0px_-1px_3px_0px_rgba(16,24,40,0.04)]"
                  key={index}
                >
                  <Page
                    pageNumber={index + 1}
                    renderAnnotationLayer={false}
                    renderTextLayer={false}
                    width={pageWidth}
                  />
                </div>
              ))}
              <div className="h-2" />
            </Document>
          ) : (
            <Loader />
          )}
        </div>

        <div className="flex justify-end gap-3 bg-gray-100 px-6 py-4">
          <Button
            className="w-fit px-6 py-2"
            onClick={handleDismiss}
            styleType="BLUE_ROUNDED"
            type="button"
          >
            <AppFormattedMessage id="CLOSE" />
          </Button>
        </div>
      </div>
    </ModalWrapper>
  );
};
